/**
 * BiomeSystem - Defines world biomes, terrain constraints, and seasonal permissions.
 * - Northern Glacial / Tundra: Always Winter ('winter' only)
 * - Central Plains / Grassland: Full Four Seasons ('spring' | 'summer' | 'autumn' | 'winter')
 * - Southern Desert / Wasteland: Always Summer ('summer' only)
 * - Underground Crypt & Interior: Indoor dungeon environment (No outdoor weather/seasons)
 */

import { isDungeonTile } from './DungeonMap';
import { SeasonType } from './EnvironmentSystem';

export type BiomeType = 'plains' | 'desert' | 'glacier' | 'dungeon';

export interface BiomeInfo {
  type: BiomeType;
  name: string;
  icon: string;
  description: string;
  allowedSeasons: SeasonType[];
  fixedSeason?: SeasonType;
  isIndoor: boolean;
}

export const BIOMES: Record<BiomeType, BiomeInfo> = {
  plains: {
    type: 'plains',
    name: '솔라리아 중앙 구릉지',
    icon: '🌿',
    description: '온화하고 비옥한 대지로 봄, 여름, 가을, 겨울의 4계절이 모두 존재합니다.',
    allowedSeasons: ['spring', 'summer', 'autumn', 'winter'],
    isIndoor: false,
  },
  desert: {
    type: 'desert',
    name: '이그니스 작열하는 황무지 사막',
    icon: '🏜️',
    description: '메마른 모래 언덕과 황무지로 일 년 내내 뜨거운 여름만 지속됩니다.',
    allowedSeasons: ['summer'],
    fixedSeason: 'summer',
    isIndoor: false,
  },
  glacier: {
    type: 'glacier',
    name: '보레아스 혹한의 빙하지대',
    icon: '🏔️',
    description: '얼어붙은 만년설과 차가운 빙벽 지대로 영구한 겨울만 지속됩니다.',
    allowedSeasons: ['winter'],
    fixedSeason: 'winter',
    isIndoor: false,
  },
  dungeon: {
    type: 'dungeon',
    name: '고대 크립트 지하 던전',
    icon: '🏰',
    description: '견고한 석조 천장 아래의 지하 구역으로 지상 날씨와 계절의 영향을 받지 않습니다.',
    allowedSeasons: [],
    isIndoor: true,
  },
};

/**
 * Determine the biome type at the given world tile coordinates (x, y)
 */
export function getBiomeAt(x: number, y: number): BiomeType {
  // 1. Check if inside underground dungeon or crypt rooms
  if (isDungeonTile(x, y) !== null) {
    return 'dungeon';
  }

  // 솔라리스 왕국 대륙 기본 지형: 솔라리아 중앙 구릉지 (온화한 봄 날씨)
  return 'plains';
}

/**
 * Get comprehensive biome metadata for the tile
 */
export function getBiomeInfo(x: number, y: number): BiomeInfo {
  const type = getBiomeAt(x, y);
  return BIOMES[type];
}
